import {
  EDatasourceType,
  IDatabaseTableModel,
  IDatasourceDto,
  isInlineCodeDatasourceType
} from "@kuika/kuika-cl-model";

/**
 * The shape of a datasource's subtree, as the Sources tree (`data-diagram-scheme-window.tsx`) draws
 * it and the Datasources landing mirrors it: a datasource holds an Actions group, a Tables group
 * when its type has tables, and — on the managed database — a Session Variables row. Pure, so the
 * two surfaces answer "what is under this datasource" from one place.
 */

/** Tables with no schema of their own are the default schema's, as SQL Server names it. */
const DEFAULT_SCHEMA_NAME: string = "dbo";

/**
 * Tables bucketed by schema, in first-seen order, each bucket keeping the order it was given. The
 * Sources tree draws one collapsible header per bucket; the landing one schema row per bucket.
 */
export const groupTablesBySchema = (tables: IDatabaseTableModel[]): Map<string, IDatabaseTableModel[]> => {
  const grouped: Map<string, IDatabaseTableModel[]> = new Map<string, IDatabaseTableModel[]>();
  tables.forEach((table: IDatabaseTableModel): void => {
    const schemaName: string = table.schema || DEFAULT_SCHEMA_NAME;
    const bucket: IDatabaseTableModel[] | undefined = grouped.get(schemaName);
    if (bucket) {
      bucket.push(table);
      return;
    }
    grouped.set(schemaName, [table]);
  });
  return grouped;
};

/**
 * Types whose node carries no Tables group: APIs, code runtimes, device connections and SAP expose
 * actions only. The Sources tree's render condition lists exactly these (plus the inline-code
 * runtimes, which `isInlineCodeDatasourceType` covers).
 */
const TABLELESS_DATASOURCE_TYPES: EDatasourceType[] = [
  EDatasourceType.RestApi,
  EDatasourceType.RestApiWOSwagger,
  EDatasourceType.CSharpFunctions,
  EDatasourceType.CustomAuthenticationProvider,
  EDatasourceType.CustomAuthorizationProvider,
  EDatasourceType.CustomVerificationCodeSenderProvider,
  EDatasourceType.CustomConnectionStringProvider,
  EDatasourceType.CustomExternalTokenValidatorProvider,
  EDatasourceType.CustomMailProvider,
  EDatasourceType.BleService,
  EDatasourceType.Mqtt,
  EDatasourceType.Blockly,
  EDatasourceType.OData,
  EDatasourceType.SAP
];

/** Whether the datasource's node shows a Tables group. Every datasource shows an Actions group. */
export const hasTablesGroup = (datasource: IDatasourceDto): boolean =>
  !TABLELESS_DATASOURCE_TYPES.includes(datasource.datasourceType) &&
  !isInlineCodeDatasourceType(datasource.datasourceType);

/**
 * Session Variables belong to the classic managed database only — the Sources tree's condition is
 * `connectionString === ""` AND the `ManagedDb` type, so the Oracle/PostgreSQL managed flavours do
 * not carry the row.
 */
export const hasSessionVariablesRow = (datasource: IDatasourceDto): boolean =>
  datasource.connectionString === "" && datasource.datasourceType === EDatasourceType.ManagedDb;

/** `schema.name`, the default schema standing in for a table that names none. */
const getQualifiedTableName = (table: IDatabaseTableModel): string =>
  `${table.schema || DEFAULT_SCHEMA_NAME}.${table.name}`;

/**
 * The id a table keeps across schema reads, and the one `ITableFolder.tableIds` holds for it: the
 * table's persisted id (the managed database's `Id` extended property), or `schema.name` where the
 * database gives it none — a connected server's schema query selects `null tableid`. Every surface
 * that files a table or matches folder membership goes through here, so they cannot disagree.
 *
 * Never `table.key`: the provider mints that Guid afresh on every schema read (`getTables`,
 * `getTableIds`, `refresh3rdPartyDsSchema`) and the backend never sends one, so a folder that held
 * keys emptied on the next read. Such legacy entries match no table and are left alone.
 */
export const getTableFolderMemberId = (table: IDatabaseTableModel): string => table.id || getQualifiedTableName(table);
